import React, { useContext, useState } from 'react';
import { Badge, Navbar, Container, Nav, NavDropdown } from 'react-bootstrap';
import { IdentityDispatchContext } from "../context/identity-context";
import { useNavigate } from 'react-router-dom';
import logoPDS from "../assets/e-SIP-logo-white.png";
import logoPDSSub from "../assets/e-SIP-logo-sub.png";
import avatar from "../assets/avatar.jpeg";
import { Link } from "react-router-dom";
import { cognitoSignOut } from "../helpr/authHelper";
import {
  SELLING_AGENT_AUTHORIZER_ROLE,
  SELLING_AGENT_ROLE,
  ADCU_ROLE,
  ISSUER_ROLE,
  INVESTOR_ROLE,
  BROKER_ROLE,
  ADMIN_SPU_ROLE,
  ADMIN_LISTING_OFFICER,
  UNDERWRITER_ROLE,
  ADCU_MAKER,
  ADCU_CHECKER,
  CA_CHECKER,
  CA_MAKER,
  RR_MAKER,
  RR_CHECKER,
  SELLING_AGENT_REPORT
} from "../helpr/constants";
import ModalCustom from "./modal/modal";
import { Security } from "../models/security";
import UserService from "../services/user-service";
import Loading from "./loading/loading";
import NotificationDropdown from './notification/Notification';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { queryClient } from '../helpr/queryClient';

/*
  Navigation - component for navigation layout
  props:
  1. displayName - display name of signed in user
  2. email - email of signed in user
  3. modules - modules to be displayed in the navigation
*/
const Navigation = ({ displayName, email, role, entity, modules = [] }) => {
  /*
    Page History for page routes on signout
  */
  const { clearIdentityContext } = useContext(IdentityDispatchContext);
  const [show, setShow] = useState(false);
  const userService = new UserService();
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  /*
    Function to decorate or accent clicked main links in the
    navigation
   */
  const decorate = (e) => {
    const links = document.getElementsByClassName('main-nav-link');
    [...links].forEach(link => link.classList.remove('active'));
    e.target.classList.add('active');
  }

  /*
    Function to sign out the user from the portal
   */
  const handleSignOut = async (e) => {
    e.preventDefault();
    try {
      console.log('Signing out...');
      await cognitoSignOut();
      navigate('/', { replace: true });
      clearIdentityContext();
      localStorage.clear();
      // remove calendar query so stale calendar data isn't shown after logout
      try {
        queryClient.removeQueries({ queryKey: ['calendarDates'] });
      } catch (err) {
        console.warn('Failed to remove calendar query on signout', err);
      }
    } catch (error) {
      console.log('error signing out:', error?.message || error);
    }
  }

  /*
    Function to show video modal
   */
  const showVideo = async () => {
    setShow(true);
    try {
      setLoading(true);
      const result = await userService.getTrainingVideoUrl();
      setVideoUrl(result.presignedUrl);
    } catch (error) {
      setShow(false);
    } finally {
      setLoading(false);
    }
  }

  /*
    Function to redirect to FAQ Page
   */
  const redirectFAQ = () => {
    navigate(`/faq`);
  }

  /*
    Function to render profile dropdown section
   */
  const renderProfileInfo = () => {
    let roleName = "";
    switch (role) {
      case SELLING_AGENT_ROLE:
        roleName = "Selling Agent";
        break;
      case SELLING_AGENT_AUTHORIZER_ROLE:
        roleName = "Selling Agent Authorizer";
        break;
      case RR_MAKER:
        roleName = "RR Maker";
        break;
      case RR_CHECKER:
        roleName = "RR Checker";
        break;
      case ADCU_ROLE:
        roleName = "ADCU";
        break;
      case ISSUER_ROLE:
        roleName = 'Issuer';
        break;
      case UNDERWRITER_ROLE:
        roleName = 'Underwriter';
        break;
      case INVESTOR_ROLE:
        roleName = "Investor";
        break;
      case BROKER_ROLE:
        roleName = "Broker";
        break;
      case ADMIN_SPU_ROLE:
        roleName = "SPU";
        break;
      case ADMIN_LISTING_OFFICER:
        roleName = "Listing Officer";
        break;
      case ADCU_MAKER:
        roleName = "ADCU Maker";
        break;
      case ADCU_CHECKER:
        roleName = "ADCU Checker";
        break;
      case CA_CHECKER:
        roleName = "CA Checker";
        break;
      case CA_MAKER:
        roleName = "CA Maker";
        break;
      case SELLING_AGENT_REPORT:
        roleName = "Selling Agent Report";
        break;
      default:
    }
    return (
      <>
        <span>{roleName}</span>{' '}
        <img alt="avatar" className="nav-link-avatar" src={avatar} />
      </>
    )
  }

  /*
    JSX Markup for Navbar containing logo, display name, email,
    and dropdown list of actions including sign out
   */
  return (
    <Navbar expand="lg" variant="light">
      <Container fluid="xl">
        <Navbar.Brand href="#home">
          <img alt="logo" className="logo" src={logoPDS} />
          <div className="vertical-bar" />
          <img alt="logo2" className="logo-sub" src={logoPDSSub} />
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="responsive-navbar-nav" />
        <Navbar.Collapse id="responsive-navbar-nav">
          <Nav className="mr-auto">
            {modules.map((route, i) => {
              return route.link && (
                <Link style={{ color: 'white' }}
                  key={i}
                  to={route.path}
                  className="main-nav-link nav-link"
                  onClick={decorate}
                >{route.name}</Link>
              )
            })}
          </Nav>

          <Nav>
            {role === ADMIN_LISTING_OFFICER && (
              <div className="nav-notification d-flex align-items-center me-3">
                {/* Notification area - NotificationDropdown now handles fetching + badge */}
                <NotificationDropdown
                  userId={undefined}
                  markReadOnOpen={false}
                  role={role}
                />
              </div>
            )}

            <NavDropdown title={renderProfileInfo()} id="collasible-nav-dropdown" className="profile-info">
              <NavDropdown.Header>
                <div className="nav-link-avatar-header">
                  {displayName}
                </div>
                <div>
                  {email}
                </div>
              </NavDropdown.Header>
              <NavDropdown.Divider />
              {role !== ADCU_ROLE && role !== ADMIN_LISTING_OFFICER
                ? <React.Fragment>
                  <NavDropdown.Item className="nav-link" href="#" onClick={showVideo}>Training Video</NavDropdown.Item>
                  <NavDropdown.Item className="nav-link" href="#" onClick={redirectFAQ}>FAQs</NavDropdown.Item>
                  <NavDropdown.Divider />
                </React.Fragment> : null}
              <NavDropdown.Item className="nav-link-signout" href="#" onClick={handleSignOut}>Sign out</NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </Container>
      <ModalCustom
        show={show}
        title={'Training Video'}
        onClose={() => setShow(false)}
        component={renderTrainingVideo(loading, videoUrl)}
        style={'video-generic'} />
    </Navbar>
  );
}

const renderTrainingVideo = (loading, url) => {
  return loading ? <Loading fullscreen={true} /> : (
    <div className={'video'}>
      <video className={'video'} controls >
        <source src={url} type="video/mp4" />
        Sorry, your browser doesn't support embedded videos.
      </video>
    </div>
  )
}

export default Navigation;
